import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

interface ResendButtonProps {
  /** Async send action. Cooldown starts only on successful send. */
  onSend: () => Promise<void> | void;
  /** Cooldown in seconds before re-enable. Default: 30. */
  cooldownSeconds?: number;
  /** Idle-state label. */
  label?: string;
  /** Cooldown-state label receiving the remaining seconds. */
  cooldownLabel?: (remaining: number) => string;
  /** Disable independent of cooldown (parent form submitting, etc). */
  disabled?: boolean;
}

/**
 * ResendButton — tertiary text button with a 30-second mono countdown after
 * a successful send. Prevents rapid-fire resends and signals to the user
 * that the system understood the request. Uses the editorial mono token
 * for the countdown so the UI looks technical rather than chatty.
 */
export function ResendButton({
  onSend,
  cooldownSeconds = 30,
  label = "Resend",
  cooldownLabel = (n) => `Resend in ${n}s`,
  disabled = false,
}: ResendButtonProps) {
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  const isCountingDown = remaining > 0;

  // Wall-clock-anchored countdown — browsers throttle setInterval in
  // hidden tabs to once-per-minute, which would otherwise leave the
  // counter showing stale "remaining" seconds long after wall-clock
  // expiry. Anchoring to performance.now() means the displayed value
  // catches up correctly on tab-resume.
  useEffect(() => {
    if (!isCountingDown || startedAt === 0) return;
    const tick = () => {
      const elapsed = Math.floor((performance.now() - startedAt) / 1000);
      const next = Math.max(0, cooldownSeconds - elapsed);
      setRemaining(next);
    };
    const id = window.setInterval(tick, 1000);
    const onVisibility = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isCountingDown, startedAt, cooldownSeconds]);

  const handleClick = async () => {
    if (sending || remaining > 0 || disabled) return;
    setSending(true);
    try {
      await onSend();
      setStartedAt(performance.now());
      setRemaining(cooldownSeconds);
    } catch {
      // Caller is responsible for surfacing the user-facing error
      // (e.g. AuthBanner). Cooldown is intentionally NOT started on
      // failure so the user can retry immediately.
    } finally {
      setSending(false);
    }
  };

  const onCooldown = remaining > 0;
  const isDisabled = disabled || sending || onCooldown;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-busy={sending || undefined}
      className="auth-tertiary inline-flex items-center gap-1.5"
    >
      {sending && (
        <Loader2 className="w-3 h-3 animate-spin motion-reduce:hidden" aria-hidden="true" />
      )}
      <span style={{ fontFamily: onCooldown ? "var(--font-mono)" : "inherit" }}>
        {sending ? "Sending…" : onCooldown ? cooldownLabel(remaining) : label}
      </span>
    </button>
  );
}
